import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useInView } from "@/hooks/useInView";
import { cn } from "@/lib/utils";
import {
  AgentGlyph,
  AutomationGlyph,
  CatalogGlyph,
  ChannelsGlyph,
  CrmGlyph,
  DealsGlyph,
  EnhanceGlyph,
  FeatureGlyphFrame,
  MetricsGlyph,
  ScheduleGlyph,
  SummaryGlyph,
  TasksGlyph,
  TimedMessageGlyph,
} from "./features/FeatureGlyphs";

type Feature = {
  glyph: ReactNode;
  title: string;
  description: string;
  href?: string;
};

const featureGroups: { label: string; items: Feature[] }[] = [
  {
    label: "Conversa e canais",
    items: [
      {
        glyph: <AgentGlyph className="h-full w-full" />,
        title: "Agentes de IA",
        description:
          "Configure papéis, tom de voz, regras de negócio, catálogo e transbordo para humano quando precisar.",
        href: "/whatsapp/agente-ia",
      },
      {
        glyph: <ChannelsGlyph className="h-full w-full" />,
        title: "WhatsApp e Instagram",
        description:
          "Centralize conversas dos canais onde seu cliente já fala com você, com histórico e contexto unificados.",
        href: "/whatsapp/agente-ia",
      },
      {
        glyph: <AutomationGlyph className="h-full w-full" />,
        title: "Automações por canal",
        description:
          "Crie fluxos por palavra-chave, primeiro contato, comentário ou story no WhatsApp e Instagram.",
        href: "/instagram/agente-ia",
      },
      {
        glyph: <EnhanceGlyph className="h-full w-full" />,
        title: "Aprimorar mensagens com IA",
        description:
          "Melhore o texto antes de enviar: mais claro, profissional e fiel ao que você quer comunicar.",
        href: "/aprimorar-mensagens",
      },
    ],
  },
  {
    label: "CRM e vendas",
    items: [
      {
        glyph: <CrmGlyph className="h-full w-full" />,
        title: "CRM e funil de leads",
        description:
          "Organize leads por estágio, tags e responsável. Ative acompanhamento automático e veja o histórico de conversas.",
        href: "/integracoes/crm",
      },
      {
        glyph: <DealsGlyph className="h-full w-full" />,
        title: "Negociações",
        description:
          "Acompanhe oportunidades comerciais em pipeline, valores e etapas ligadas aos seus leads.",
        href: "/negociacoes",
      },
      {
        glyph: <CatalogGlyph className="h-full w-full" />,
        title: "Catálogo de produtos",
        description:
          "Cadastre produtos e serviços no painel e deixe o agente responder preços e disponibilidade na conversa.",
        href: "/catalogo-de-produtos",
      },
    ],
  },
  {
    label: "Operação do dia",
    items: [
      {
        glyph: <ScheduleGlyph className="h-full w-full" />,
        title: "Agendamentos",
        description:
          "Agenda automática com Google Calendar, confirmações e gestão de horários pelo agente ou pela equipe.",
      },
      {
        glyph: <TimedMessageGlyph className="h-full w-full" />,
        title: "Agendar mensagens",
        description:
          "Programe envios e mensagens de encerramento para o cliente certo, na hora certa.",
        href: "/agendar-mensagens",
      },
      {
        glyph: <TasksGlyph className="h-full w-full" />,
        title: "Tarefas e gamificação",
        description:
          "Distribua pendências, marque urgências e acompanhe o progresso do dia com metas visuais para a equipe.",
        href: "/tarefas",
      },
      {
        glyph: <SummaryGlyph className="h-full w-full" />,
        title: "Resumir atendimento",
        description:
          "Gere um resumo da conversa em segundos para passar o caso, encerrar ou alinhar a equipe.",
        href: "/analise-de-atendimento",
      },
    ],
  },
  {
    label: "Visão geral",
    items: [
      {
        glyph: <MetricsGlyph className="h-full w-full" />,
        title: "Dashboard e métricas",
        description:
          "Acompanhe leads, conversas, desempenho da equipe, tarefas e horários de pico em gráficos claros.",
        href: "/analise-de-atendimento",
      },
    ],
  },
];

const allFeatures = featureGroups.flatMap((group) => group.items);

type EnterDelay = "none" | "short" | "medium" | "long";

const enterDelayClass: Record<EnterDelay, string> = {
  none: "",
  short: "motion-safe:delay-75",
  medium: "motion-safe:delay-150",
  long: "motion-safe:delay-225",
};

function enterMotion(isInView: boolean, delay: EnterDelay = "none") {
  return cn(
    "motion-safe:transition-[opacity,transform] motion-safe:duration-700 motion-safe:ease-[cubic-bezier(0.22,1,0.36,1)]",
    enterDelayClass[delay],
    !isInView &&
      "translate-y-3 opacity-0 motion-reduce:translate-y-0 motion-reduce:opacity-100",
    isInView && "translate-y-0 opacity-100",
  );
}

function FeatureCard({
  feature,
  isInView,
  index,
}: {
  feature: Feature;
  isInView: boolean;
  index: number;
}) {
  return (
    <article
      className={cn(
        "flex h-full flex-col items-start rounded-xl border border-border/70 bg-card p-5 text-left",
        "motion-safe:transition-[border-color,background-color,box-shadow,transform] motion-safe:duration-300",
        "hover:border-primary/20 hover:bg-card hover:shadow-[0_8px_24px_-8px_hsl(var(--primary)/0.18)] motion-safe:hover:-translate-y-0.5",
        enterMotion(isInView, index % 4 === 0 ? "none" : index % 4 === 1 ? "short" : index % 4 === 2 ? "medium" : "long"),
      )}
      style={{ transitionDelay: isInView ? `${index * 40}ms` : undefined }}
    >
      <FeatureGlyphFrame>{feature.glyph}</FeatureGlyphFrame>
      <h3 className="mb-2 text-base font-semibold tracking-tight text-foreground">
        {feature.href ? (
          <Link to={feature.href} className="hover:text-primary hover:underline underline-offset-4">
            {feature.title}
          </Link>
        ) : (
          feature.title
        )}
      </h3>
      <p className="text-sm leading-relaxed text-foreground/72">{feature.description}</p>
    </article>
  );
}

export const Features = () => {
  const { ref, isInView } = useInView({ threshold: 0.1 });

  return (
    <section
      id="features"
      className="relative scroll-mt-24 overflow-hidden border-t border-border/40 bg-background py-20 md:py-28"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_70%_45%_at_50%_0%,hsl(var(--primary)/0.06),transparent_55%)]"
        aria-hidden
      />

      <div className="container relative mx-auto max-w-6xl px-4">
        <div
          ref={ref}
          className={cn(
            "mb-12 max-w-3xl md:mb-14",
            enterMotion(isInView),
          )}
        >
          <h2 className="text-pretty text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-5xl">
            Simples para você.{" "}
            <span className="text-primary">Poderoso para seus clientes</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-foreground/72 md:text-lg">
            Do primeiro &ldquo;oi&rdquo; no WhatsApp ao acompanhamento no CRM, tudo no mesmo painel, sem
            trocar de ferramenta.
          </p>
        </div>

        <div className="space-y-10 md:space-y-12">
          {featureGroups.map((group, groupIndex) => (
            <div key={group.label}>
              <p
                className={cn(
                  "mb-4 text-sm font-medium text-foreground/75",
                  enterMotion(isInView, groupIndex === 0 ? "short" : groupIndex === 1 ? "medium" : "long"),
                )}
              >
                {group.label}
              </p>
              <div
                className={cn(
                  "grid gap-4",
                  group.items.length === 1
                    ? "grid-cols-1 sm:max-w-sm"
                    : group.items.length === 3
                      ? "sm:grid-cols-2 lg:grid-cols-3"
                      : "sm:grid-cols-2 lg:grid-cols-4",
                )}
              >
                {group.items.map((feature) => {
                  const index = allFeatures.indexOf(feature);
                  return (
                    <FeatureCard
                      key={feature.title}
                      feature={feature}
                      isInView={isInView}
                      index={index}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
